<template>
  <b-container class="chat-box">
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @go-to-step="goToStep"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Demo pública de Corrección de Tests: reemplaza al corrector real cuando el
// visitante no tiene sesión (corregir de verdad gasta un `tests_uses`).
//
// Misma maquinaria que el resto de las demos de servicio —ChatMessageComponent
// + un grafo `questions`—. Abre con lo mismo que cuenta
// /correccion-de-tests/ —el cuerpo de esa página, con su historia y
// sus animaciones (CorreccionSecciones)— y después el menú: la corrección
// pregrabada (TestCorrectionDemoPlayer), preguntas frecuentes, precios o
// iniciar sesión. Las preguntas y los planes bajan del endpoint de la página,
// igual que en las demás demos.
//
// URL estable: /utopie#/test-correction-demo (ruta `test-correction-demo`). Es
// el destino de los CTA de las páginas indexables de SEO y de los links de
// invitación (ver DEMO_ROUTE en InviteFriendsView).
import ChatMessageComponent from "./ChatMessageComponent.vue";
import TestCorrectionDemoPlayer from "./TestCorrectionDemoPlayer.vue";
import CorreccionSecciones from "./CorreccionSecciones.vue";
import informationService from "@/services/informationService";
import { fetchRewards, applyBonus, loginLabel } from "./signupRewards";
import { sectionStep, sectionComponent } from "./serviceSections";
import { hayTicketVisible } from "./ticketFocus";

// El café de Corrección de Tests (`accent_key='brown'` en pages.py) y su
// versión oscura para texto.
const ACENTO = "#b76629";
const ACENTO_INK = "#b06227";

// Las salidas del menú. `option: "login"` lo intercepta `continueChat` (abre el
// modal de ingreso); el resto son pasos del grafo.
const MENU_OPTIONS = [
  {
    name: "Iniciar sesión para probar la versión real",
    cardClass: "highlighted-option pulse-option",
    option: "login",
  },
  {
    name: "Ver el demo de una corrección",
    nextQuestionIndex: "demo",
    // La demo se puede repetir: es la acción que engancha, no un texto que ya
    // se leyó.
    alwaysShow: true,
  },
  {
    name: "Conocer más sobre el servicio",
    nextQuestionIndex: "details",
  },
  {
    name: "Ver las preguntas frecuentes",
    nextQuestionIndex: "faq",
  },
  {
    name: "Ver los precios y planes",
    nextQuestionIndex: "prices",
  },
  // {
  //   name: "Recibir una cotización para mi colegio",
  //   nextQuestionIndex: "bill",
  // },
];

const toAnswer = (ans) => ({
  ...ans,
  isSelected: false,
  class: "col-md-7 col-12 my-2",
  bootstrap_classes: "text-left px-2 font-weight-bold",
});

export default {
  name: "TestCorrectionDemo",
  components: { ChatMessageComponent },
  // Absorbe las props que el layout bindea en su router-view, para que no se
  // filtren al elemento raíz; la demo no usa ninguna.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      // Mismo par de colores que el producto (--ct-product) y que el chatbot
      // con sesión, para que la demo y lo que sigue se vean como lo mismo.
      mainColor: "#b76629",
      secondaryColor: "#FFD6AF",
      currentQuestionIndex: "",
      condition: "",
      context: { service: "Corregir Test" },
      messages: [],
      questions: {
        // La llegada: el saludo y el cuerpo de la página de producto. El menú
        // aparece solo debajo, y como al llegar queda `details` en `context`,
        // el menú deja de ofrecerlo.
        details: sectionStep("details", "👋 ¡Hola!", {
          component: CorreccionSecciones,
          dontShowNextButton: true,
          nextQuestionIndex: "menu",
          // El botón de Google va bajo el saludo, para entrar sin bajar hasta
          // el menú.
          loginButton: true,
        }),
        // La corrección pregrabada, como un paso del chat. Pide `go-to-step`
        // para volver al menú o abrir el login.
        demo: {
          name: "demo",
          text: "¡Perfecto! Este protocolo ya viene aplicado: mira cómo se corrige solo y qué entrega al final.",
          type: "custom-component",
          component: TestCorrectionDemoPlayer,
          answersLoaded: true,
          answers: [],
          dontShowNextButton: true,
          nextQuestionIndex: "menu",
          // Tener getAnswers evita que ChatMessageComponent emita `continue`
          // solo: el paso avanza cuando el reproductor lo pide.
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        faq: sectionStep(
          "faq",
          "Estas son las preguntas que más nos hacen sobre Corrección de Tests:",
          { dontShowNextButton: true, nextQuestionIndex: "menu" }
        ),
        prices: sectionStep(
          "prices",
          "Así puedes usar Corrección de Tests, según lo que necesites:",
          { dontShowNextButton: true, nextQuestionIndex: "menu" }
        ),
        bill: {
          name: "bill",
          text: "¡Excelente! Indícanos tu correo y la cantidad de correcciones a cotizar y te enviaremos la cotización.",
          type: "inputs",
          answersLoaded: true,
          answers: [
            {
              name: "email",
              type: "email",
              placeholder: "Escribe tu correo aquí",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "applications",
              type: "number",
              placeholder: "Cantidad de correcciones",
              class: "col-12 col-md-6 mb-2",
            },
          ],
          nextMessage: "Obtener cotización!",
          extraFunction: (data) => {
            informationService
              .sendEurekaBill({
                email: data.email,
                applications: data.applications,
                service: "TEST",
              })
              .then((resp) => {
                this.$toasted.success(resp.message);
              });
          },
          nextQuestionIndex: "menu",
          routine: () => {},
        },
        // El menú, al llegar y de vuelta: una opción ya usada (su paso quedó
        // como clave en `context`) no reaparece, y los objetos nuevos evitan
        // que se arrastre un `isSelected` anterior.
        menu: {
          name: "menu",
          text: "<strong>¿Qué te gustaría hacer?</strong>",
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: MENU_OPTIONS,
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  ans.alwaysShow ||
                  !ans.nextQuestionIndex ||
                  !(ans.nextQuestionIndex in context)
              )
              .map(toAnswer);
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
      },
    };
  },
  created() {
    this.$store.commit("chatbot/clear");
    // Los usos gratis salen de la config y las preguntas y los planes, de la
    // página de producto. Si alguna falla, esa mitad simplemente no se cuenta.
    Promise.all([
      fetchRewards("Corregir Test"),
      informationService.getServiceContent("correccion-de-tests").catch(() => null),
    ]).then(([rewards, content]) => {
      this.applyContent(content);
      applyBonus(this.questions, rewards, ["menu"]);
      this.questions.details.loginLabel = loginLabel(rewards, "Iniciar sesión para probar la versión real");
      this.currentQuestionIndex = "details";
      const question = this.questions[this.currentQuestionIndex];
      this.context[question.name] = {};
      this.messages.push(question);
    });
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  watch: {
    // Once the visitor logs in (from a CTA), send them to the real product.
    token(val) {
      if (val) this.goToRealProduct();
    },
  },
  mounted() {
    // A logged-in user has no business on the demo (e.g. landed here after a
    // login that reloaded the page) — forward them to the real chatbot.
    if (this.token) this.goToRealProduct();
  },
  methods: {
    // Reparte el contenido de la página de producto. Sin contenido —el
    // endpoint no respondió— el FAQ y los precios no tienen de dónde salir y
    // se retiran del menú: mejor no ofrecerlos que abrir una sección vacía.
    applyContent(content) {
      if (content) {
        if (content.hero_lede) {
          this.questions.details.text += ` ${content.hero_lede}`;
        }
        this.questions.faq.component = sectionComponent(
          content,
          "faq",
          ACENTO,
          ACENTO_INK
        );
        this.questions.prices.component = sectionComponent(
          content,
          "planes",
          ACENTO,
          ACENTO_INK
        );
        return;
      }
      const menu = this.questions.menu;
      menu.process_dic = menu.process_dic.filter(
        (ans) => !["faq", "prices"].includes(ans.nextQuestionIndex)
      );
    },
    handleCardClick(answer, msgidx, ansidx) {
      if (this.messages[msgidx].name !== this.currentQuestion.name) return;
      this.selectCard(msgidx, ansidx);
      this.continueChat();
    },
    selectCard(msgidx, ansidx) {
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      this.messages[msgidx].answers[ansidx].isSelected = true;
    },
    continueChat() {
      const question = this.currentQuestion;
      if (question.type === "options" && question.single_choice) {
        const selected = question.answers.find((ans) => ans.isSelected);
        this.context[question.name] = selected;
        if (selected && selected.option === "login") {
          this.login();
          return;
        }
      }
      this.messages.slice(-1)[0].answered = true;
      const selected = this.context[question.name];
      this.currentQuestionIndex =
        selected && selected.nextQuestionIndex
          ? selected.nextQuestionIndex
          : question.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      const question = this.questions[questionIndex];
      if (!question) return;
      this.context[question.name] = {};
      this.messages.push(question);
    },
    // El reproductor y los botones de los planes piden volver al menú o abrir
    // el login por acá.
    goToStep(step) {
      if (step === "login") {
        this.login();
        return;
      }
      if (!this.questions[step]) return;
      this.messages.slice(-1)[0].answered = true;
      this.currentQuestionIndex = step;
      this.loadQuestion(step);
    },
    scrollToBottom() {
      // Con un golden ticket sin canjear arriba, la demo no mueve el scroll:
      // quien llego escaneando el QR vino por el ticket, no por la demo.
      if (hayTicketVisible()) return;
      const container = document.querySelector(".general-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      if (hayTicketVisible()) return;
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // Las secciones sin botón —la llegada, el FAQ y los precios— se
      // encadenan solas con el menú: hacerle caso al scroll dejaría al
      // visitante debajo de lo que acaba de abrir. El reproductor también lleva
      // `dontShowNextButton`, pero con getAnswers: el menú llega porque el
      // visitante lo pidió, así que ahí sí se sigue.
      const idx = this.messages.map((m) => m.name).lastIndexOf(name);
      const previo = idx > 0 ? this.messages[idx - 1] : null;
      if (previo && previo.dontShowNextButton && !previo.getAnswers) return;
      const parentMsg = msgRef[msgRef.length - 1].$el.parentNode;
      // "start", no "center": un mensaje más alto que la pantalla (la
      // corrección completa) dejaría al visitante en la mitad.
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    },
    login() {
      // `register: false` abre el modal en el estado de ingreso, que trae el
      // botón de Google MÁS correo y contraseña; el de registro solo muestra
      // Google y dejaba sin puerta a quien ya tiene cuenta.
      this.$emit("open-login", { register: false });
    },
    goToRealProduct() {
      if (this.$router.currentRoute.name !== "corregir-test") {
        this.$router.push("corregir-test").catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.chat-box {
  margin: 10px auto;
  width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
</style>
